// El enlace entre un cobro y su cotización, cuando se confirma el pago inicial.
//
// ┌─ POR QUE ESTE ARCHIVO EXISTE ────────────────────────────────────────────────┐
// │ Al confirmar un pago inicial, `updateCobro` avanza el onboarding de la       │
// │ cotización. Ese bloque entero está condicionado a HABER ENCONTRADO la        │
// │ cotización en `data.cotizacionesEnviadas` — la cache en memoria del front.   │
// │                                                                              │
// │ Si el cobro DECLARA una cotización (`cobros.quote_id`) pero esa fila no      │
// │ aparece, el bloque se salta ENTERO y no pasa nada: ni el avance de estado,   │
// │ ni la notificación, ni un aviso. El pago queda confirmado y la cotización    │
// │ nunca avanza. **En un flujo de dinero, ese silencio es el defecto.**         │
// └──────────────────────────────────────────────────────────────────────────────┘
//
// CUANDO PUEDE PASAR — configuracion SOPORTADA, no hipotetica:
// `finanzas` es un rol de primera clase del producto (`permissions.js`) y su preset incluye
// el modulo Finanzas, desde donde se confirman los cobros. Pero la RLS de
// `cotizaciones_enviadas` da SELECT/UPDATE **solo a `admin` y `crm`**. El front autoriza por
// MODULO y la base autoriza por ROL: dos politicas distintas para la misma accion.
// Un usuario `finanzas` entra legitimamente a Cobros, confirma, y la cotizacion le es
// INVISIBLE — no por un error suyo, sino por esa divergencia.
//
// ⚠️ ESTO NO ARREGLA EL PERMISO, Y NO PRETENDE HACERLO.
// Dar o no dar acceso a `finanzas` sobre las cotizaciones es una decision de permisos que
// necesita su propio OK. Lo que hace este modulo es que el hueco **deje de ser silencioso**:
// avisar bien de un permiso que falta y otorgar el permiso son dos arreglos distintos.

// ¿El cobro afirma pertenecer a una cotización? Se mira el dato, no la suerte de haberla
// encontrado: `cobros.quote_id` es el enlace explícito, y `payment_source` la marca del flujo.
export function elCobroDeclaraCotizacion({ cobro, cambios } = {}) {
  const idDeclarado = cambios?.quote_id || cobro?.quote_id || '';
  if (String(idDeclarado || '').trim()) return true;
  const origen = String(cambios?.payment_source || cobro?.payment_source || '').toLowerCase();
  return origen === 'quote_initial_payment';
}

// 'encontrada'             → hay cotización y se puede avanzar su onboarding
// 'sin_cotizacion'         → el cobro no pertenece a ninguna; saltar es CORRECTO
// 'declarada_no_visible'   → el cobro dice tener una y NO aparece: hay que decirlo
export function diagnosticoDeCotizacionDelCobro({ cobro, cambios, cotizacionEncontrada } = {}) {
  if (cotizacionEncontrada?.id) return 'encontrada';
  return elCobroDeclaraCotizacion({ cobro, cambios }) ? 'declarada_no_visible' : 'sin_cotizacion';
}

// Voz de STAFF. Lo que más importa que se lea: el cobro SÍ se guardó, y algo quedó a medias.
// No se culpa al usuario ni se le pide que "revise permisos": se le dice qué no ocurrió.
export const AVISO_COTIZACION_NO_VISIBLE =
  'Cobro actualizado, pero no pudimos avanzar la cotización vinculada: no aparece con tu acceso. Pídele a administración que confirme el estado de la cotización.';

// ── LA FRONTERA SERVER-SIDE (migración 184) ──────────────────────────────────────────
// 🔴 EL UPDATE DIRECTO ERA LA CAUSA, NO EL SÍNTOMA. El front escribía en
// `cotizaciones_enviadas`, y esa tabla solo da UPDATE a `admin`/`crm`: un usuario `finanzas`
// —rol de primera clase del producto— confirmaba el cobro y el UPDATE tocaba 0 filas.
// El aviso de arriba hizo que dejara de ser silencioso; **esto le da la capacidad**.
//
// UN SOLO CAMINO CAUSAL, a propósito: admin, crm y finanzas pasan todos por la RPC. Mantener
// «admin escribe directo, finanzas usa RPC» serían dos implementaciones del mismo hecho, que
// divergen en cuanto una de las dos cambie.
//
// La RPC resuelve la cotización DESDE EL COBRO en el servidor, así que **ya no depende de que
// el front tenga la fila en su caché** — que era la otra mitad del hueco.
//
// ⚠️ «LA RPC RESPONDIÓ» NO ES «EL EFECTO OCURRIÓ»: devuelve el id de la cotización avanzada, y
// solo eso cuenta como éxito. Un error de permiso, un cobro sin cotización y un fallo real son
// tres estados distinguibles.
export async function avanzarCotizacionPorCobro(supabase, cobroId, { metodo, monto, confirmadoAt } = {}) {
  const { data, error } = await supabase.rpc('finanzas_cerrar_pago_inicial_cotizacion', {
    p_cobro_id: cobroId,
    p_metodo: metodo || null,
    p_monto: Number.isFinite(Number(monto)) ? Number(monto) : null,
    p_confirmado_at: confirmadoAt || new Date().toISOString(),
  });

  if (error) {
    const texto = `${error.message || ''} ${error.details || ''}`.toLowerCase();
    // La RPC distingue estos casos con errcode; se leen por mensaje porque PostgREST no
    // expone el SQLSTATE original de forma estable.
    if (texto.includes('no esta relacionado con ninguna cotizacion')) return { estado: 'sin_cotizacion' };
    if (texto.includes('no tienes permiso')) return { estado: 'sin_permiso', error };
    return { estado: 'fallido', error };
  }
  // Sin id devuelto no hay efecto que afirmar.
  return data ? { estado: 'avanzada', quoteId: data } : { estado: 'fallido' };
}

// Qué se le dice al staff según lo que REALMENTE pasó.
const AVISOS_RPC = {
  avanzada: null,                       // éxito: el toast normal del cobro alcanza
  sin_cotizacion: null,                 // legítimo: el cobro no pertenece a ninguna. Sin warning falso.
  sin_permiso: 'Cobro actualizado, pero tu usuario no puede avanzar la cotización vinculada. Pídeselo a administración.',
  fallido: 'Cobro actualizado, pero no pudimos avanzar la cotización vinculada. Revísala antes de enviar accesos.',
};

// ⚠️ `??` NO sirve acá: `null` es un valor LEGÍTIMO —significa «no hay nada que avisar»— y
// `null ?? fallido` devuelve el warning. Es decir, los dos casos sanos («avanzada» y
// «sin_cotizacion») habrían mostrado «no pudimos avanzar la cotización»: el warning falso
// exacto que este módulo existe para evitar. Se pregunta si la clave EXISTE, no si tiene valor.
export function avisoDeAvanceDeCotizacion(estado) {
  return Object.prototype.hasOwnProperty.call(AVISOS_RPC, estado)
    ? AVISOS_RPC[estado]
    : AVISOS_RPC.fallido;
}
